import {
  addMailingWithEmails,
  createEntityAction,
  deleteEmailFromMailing,
  deleteEntityAction,
  getDataForAdmin,
  updateEntityAction,
} from '../../redux/actions/admin.action';
import {
  initOptionsDirectoratesFromServer,
  initDepartmentOptionsFromServer,
  getPositionsFromServer,
  initAllDepartmentOptionsFromServer,
  initAllOptionsDirectoratesFromServer,
  getRolesFromServer,
  getRoadsFromServer,
} from '../../redux/actions/options.action';
import { convertDateToString } from '../../utils/convertToDate';

export const API_PATHS = {
  roads: '/api/administration/roads',
  directorates: '/api/administration/directorates',
  departments: '/api/administration/departments',
  brigades: '/api/administration/brigades',
  employees: '/api/administration/employees',
  users: '/api/administration/users',
  mailings: '/api/administration/mailings',
  logs: '/api/administration/users/logs',
};

export const getTranslatedAction = (action, t) => {
  const actionMap = {
    CREATE: t?.actionCreate || 'Создание',
    UPDATE: t?.actionUpdate || 'Изменение',
    DELETE: t?.actionDelete || 'Удаление',
  };

  return actionMap[action] || action || '-';
};

/**
 * Создает набор Redux-действий (actions) для работы с административными сущностями.
 *
 * @param {string} entityKey - Ключ сущности (например, 'roads', 'users'), используется для генерации путей и идентификации.
 * @param {Object} [options={}] - Дополнительные параметры конфигурации.
 * @param {boolean} [options.includeDelete=false] - Включить ли действие удаления.
 * @param {Object} [options.overrides={}] - Объект с переопределениями стандартных действий.
 * @param {Function} [options.overrides.add] - Кастомная функция для добавления сущности.
 * @param {Function} [options.overrides.edit] - Кастомная функция для редактирования сущности.
 * @param {Function} [options.overrides.get] - Кастомная функция для получения списка сущностей.
 * @param {Function} [options.overrides.delete] - Кастомная функция для удаления сущности.
 *
 * @returns {Object} Объект с действиями:
 * - `add` - действие для добавления сущности (по умолчанию `createEntityAction`)
 * - `edit` - действие для редактирования сущности (по умолчанию `updateEntityAction`)
 * - `get` - действие для получения сущностей (по умолчанию `getDataForAdmin`)
 * - `delete` - действие для удаления сущности, если включено `includeDelete` (по умолчанию `deleteEntityAction`)
 *
 * @example
 * const actions = createActions('users', {
 *   includeDelete: true,
 *   overrides: {
 *     add: customAddUser,
 *   },
 * });
 */
export const createActions = (entityKey, { includeDelete = false, overrides = {} } = {}) => {
  const basePath = API_PATHS[entityKey];

  return {
    add: overrides.add || createEntityAction(entityKey, basePath),
    edit: overrides.edit || updateEntityAction(entityKey, basePath),
    get: overrides.get || (() => getDataForAdmin(entityKey, basePath)),
    ...(includeDelete && {
      delete: overrides.delete || deleteEntityAction(entityKey, basePath),
    }),
  };
};

const validateEmail = (value, errorText) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) return errorText;
  return null;
};

// Общий размер для всех полей формы (высота и ширина)
const defaultSize = {
  width: '100%',
  height: 'var(--sadr-font-size-60)',
};

// подстановка изменяемой сущности в тайтл
export const formatEntityTitle = (baseText, entity) => {
  return `${baseText}${entity ? ` «${entity}»` : ''}`;
};

export const formatEntityTitleDelete = (template, entityName) => {
  return template?.replace('{{name}}', entityName || '');
};

/**
 * Возвращает список email-адресов для выбранной рассылки по её ID.
 *
 * @param {Object} backendData - данные из Redux (state.adminData)
 * @param {Object} context - параметры фильтрации
 * @param {number} context.selectedId - выбранный ID рассылки
 * @returns {Array<Object>} список email-объектов: [{ id, name }]
 */
export const getRecipientsFromSelectedMailing = (backendData, { selectedId }) => {
  const mailingList = backendData?.mailings || [];
  const selectedMailing = mailingList.find((mailingItem) => mailingItem.keyName === selectedId);

  if (!selectedMailing?.recipients) return [];

  return selectedMailing.recipients.map((recipient) => ({
    ...recipient,
    keyName: selectedId,
  }));
};

/**
 * Получает объект должности по `positionId` из списка `positions`.
 *
 * @param {Object} formData - Объект с текущими значениями формы.
 * @param {Object} options - Объект со всеми загруженными опциями (включая positions).
 * @returns {Object|undefined} - Найденный объект должности или undefined, если не найден.
 */
const getSelectedPosition = (formData, options) => {
  const positionId = formData?.positionId;
  const positions = options?.positions || [];
  return positions.find((position) => position.value === positionId);
};

/**
 * Поле `roadId` становится обязательным, если для выбранной должности
 * обязательны либо дирекция, либо подразделение.
 *
 * @param {Object} formData
 * @param {Object} options
 * @returns {boolean}
 */
export const isRoadRequired = (formData, options) => {
  const selectedPosition = getSelectedPosition(formData, options);
  // Если указано, что нужно указать дирекцию или подразделение, то нужна и дорога
  return !!(selectedPosition?.isDepartmentRequired || selectedPosition?.isDirectorateRequired);
};

/**
 * Поле `directorateId` становится обязательным, если у выбранной должности
 * явно указано `isDirectorateRequired`.
 *
 * @param {Object} formData
 * @param {Object} options
 * @returns {boolean}
 */
export const isDirectorateRequired = (formData, options) => {
  const selectedPosition = getSelectedPosition(formData, options);
  return !!selectedPosition?.isDirectorateRequired;
};

/**
 * Поле `departmentId` становится обязательным, если для выбранной должности
 * явно указано, что подразделение требуется.
 *
 * @param {Object} formData - Текущее состояние формы.
 * @param {Object} options - Объект с доступными опциями (включая список должностей).
 * @returns {boolean} - true, если поле `departmentId` должно быть обязательным.
 */
export const isDepartmentRequired = (formData, options) => {
  const selectedPosition = getSelectedPosition(formData, options);
  // Проверяем, указано ли, что подразделение обязательно для этой должности
  return !!selectedPosition?.isDepartmentRequired;
};

/**
 * Поле `positionFullName` становится обязательным, если у выбранной должности
 * указано `isFullNameRequired`.
 *
 * @param {Object} formData
 * @param {Object} options
 * @returns {boolean}
 */
export const isPositionFullNameRequired = (formData, options) => {
  const selectedPosition = getSelectedPosition(formData, options);
  return !!selectedPosition?.isFullNameRequired;
};

/**
 * Конфигурация текста для кнопок и сообщений в административной панели.
 */

const defaultTextConfig = (t) => ({
  addButtonText: t?.addButtonText,
  saveButtonText: t?.saveButtonText,
  editButtonText: t?.editButtonText,
  cancelButtonText: t?.cancelButtonText,
  confirmAddText: t?.confirmAddText,
  confirmEditText: t?.confirmEditText,
  returnToListText: t?.returnToListText,
});

/**
 * Конфигурация всех сущностей, доступных в административной панели.
 * Используется для генерации таблицы, формы и логики отправки данных.
 */
export const getAdminConfig = (t) => ({
  /**
   * Дороги
   */
  roads: {
    ...defaultTextConfig(t),
    label: t?.road, // название сущности для отображения в UI на кнопке
    showEditButton: true, // показывать кнопку "Редактировать"
    showDeleteButton: false, // скрыть кнопку "Удалить"
    apiPath: API_PATHS.roads, // путь к API
    actions: createActions('roads'), // экшены для работы с сущностью
    addLabel: t?.addNewRoad, // текст кнопки добавления
    titleAdd: t?.titleAddRoad, // заголовок окна добавления
    titleEdit: (entity) => formatEntityTitle(t?.titleEditRoad, entity), // заголовок окна редактирования
    titleAddResult: (entity) => formatEntityTitle(t?.titleAddResultRoad, entity), // заголовок подтверждения добавления
    titleEditResult: (entity) => formatEntityTitle(t?.titleEditResultRoad, entity), // заголовок подтверждения редактирования
    hiddenFields: ['id'], // поля, которые не нужно отображать в форме но должны быть в запросе
    // columns - массив объектов с настройками колонок
    // accessor - ключ поля в ответе от сервера
    // disableSorting - отключение сортировки по колонке
    // Header - название колонки в таблице
    columns: [
      { Header: t?.tableId, accessor: 'id', disableSorting: false },
      { Header: t?.tableRoad, accessor: 'name', disableSorting: false },
    ],
    // поля формы для создания/редактирования сущности
    formFields: [
      {
        name: 'name', // ключ поля
        label: t?.labelRoad, // метка
        placeholder: t?.placeholderEnterRoad, // плейсхолдер
        type: 'text', // тип инпута
        required: true, // обязательность
        column: 'center', // колонка в UI
        ...defaultSize,
      },
    ],
  },

  /**
   * Дирекции
   */
  directorates: {
    ...defaultTextConfig(t),
    label: t.directorate,
    showEditButton: true,
    showDeleteButton: false,
    apiPath: API_PATHS.directorates,
    actions: createActions('directorates'),
    addLabel: t?.addNewDirectorate,
    titleAdd: t?.titleAddDirectorate,
    titleEdit: (entity) => formatEntityTitle(t?.titleEditDirectorate, entity),
    titleAddResult: (entity) => formatEntityTitle(t?.titleAddResultDirectorate, entity),
    titleEditResult: (entity) => formatEntityTitle(t?.titleEditResultDirectorate, entity),
    hiddenFields: ['id'],
    excludedFields: ['roadName'], // поля исключаемые из начального объекта который сравнивается с объектом формы для активации кнопки
    // колонки для отображения в таблице
    columns: [
      { Header: t?.tableId, accessor: 'id', disableSorting: false },
      { Header: t?.tableDirectorate, accessor: 'name', disableSorting: false },
      { Header: t?.tableFullName, accessor: 'note', disableSorting: false },
      { Header: t?.tableDevice, accessor: 'device', disableSorting: false },
      { Header: t?.tableRoad, accessor: 'roadName', disableSorting: false },
    ],
    // поля формы для создания/редактирования сущности
    formFields: [
      {
        name: 'name',
        label: t?.labelDirectorate,
        placeholder: t?.placeholderEnterDirectorate,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'note',
        label: t?.labelFullName,
        placeholder: t?.placeholderFullName,
        type: 'text',
        required: true,
        column: 'right',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'device',
        label: t?.labelDevice,
        type: 'checkbox',
        required: false,
        column: 'left',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'roadId',
        label: t?.labelRoad,
        placeholder: t?.placeholderRoad,
        type: 'select',
        isMulti: false,
        required: true,
        column: 'right',
        optionsSource: 'roads',
        fetchAction: getRoadsFromServer, // если нужны опции с сервера — указать здесь
        ...defaultSize,
      },
    ],
    customRender: {
      device: (value) => (value ? t.yes : t.no), // для отрисовки булевого значения в таблице
    },
  },

  /**
   * Подразделения
   */
  departments: {
    ...defaultTextConfig(t),
    label: t?.department,
    showEditButton: true,
    showDeleteButton: false,
    apiPath: API_PATHS.departments,
    actions: createActions('departments'),
    addLabel: t?.addNewDepartment,
    titleAdd: t?.titleAddDepartment,
    titleEdit: (entity) => formatEntityTitle(t?.titleEditDepartment, entity),
    titleAddResult: (entity) => formatEntityTitle(t?.titleAddResultDepartment, entity),
    titleEditResult: (entity) => formatEntityTitle(t?.titleEditResultDepartment, entity),
    hiddenFields: ['id', 'directorateName'],
    excludedFields: ['directorateName'],
    // колонки для отображения в таблице
    columns: [
      { Header: t.tableId, accessor: 'id', disableSorting: false },
      { Header: t.tableDepartment, accessor: 'name', disableSorting: false },
      { Header: t.tableDirectorate, accessor: 'directorateName', disableSorting: false },
      { Header: t.tableFullName, accessor: 'fullName', disableSorting: false },
      { Header: t.tableShiftStart, accessor: 'shiftStart', disableSorting: false },
      { Header: t.tableShiftEnd, accessor: 'shiftEnd', disableSorting: false },
    ],
    // поля формы для создания/редактирования сущности
    formFields: [
      {
        name: 'name',
        label: t.labelDepartment,
        placeholder: t.placeholderEnterDepartment,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'fullName',
        label: t.labelFullName,
        placeholder: t.placeholderFullName,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 30,
        ...defaultSize,
      },
      {
        name: 'directorateId',
        label: t.labelDirectorate,
        placeholder: t.placeholderDirectorate,
        type: 'select',
        isMulti: false,
        required: true,
        column: 'right',
        optionsSource: 'directorates', // источник опций
        fetchAction: initAllOptionsDirectoratesFromServer, // если нужны опции с сервера — указать здесь экшен для запрос
        ...defaultSize,
      },
      {
        name: 'shiftStart',
        label: `${t.tableShiftStart} *`,
        placeholder: t.placeholderTime,
        type: 'time',
        required: true,
        column: 'left',
        ...defaultSize,
      },
      {
        name: 'shiftEnd',
        label: `${t.tableShiftEnd} *`,
        placeholder: t.placeholderTime,
        type: 'time',
        required: true,
        column: 'right',
        selfAlign: 'end',
        rowSpan: 2,
        ...defaultSize,
      },
    ],
  },

  /**
   * Бригады
   */
  brigades: {
    ...defaultTextConfig(t),
    label: t.brigade,
    showEditButton: true,
    showDeleteButton: false,
    apiPath: API_PATHS.brigades,
    actions: createActions('brigades'),
    addLabel: t?.addNewBrigade,
    titleAdd: t?.titleAddBrigade,
    titleEdit: (entity) => formatEntityTitle(t.titleEditBrigade, entity),
    titleAddResult: (entity) => formatEntityTitle(t.titleAddResultBrigade, entity),
    titleEditResult: (entity) => formatEntityTitle(t.titleEditResultBrigade, entity),
    hiddenFields: ['id'],
    excludedFields: ['departmentName'],
    // колонки для отображения в таблице
    columns: [
      { Header: t.tableId, accessor: 'id', disableSorting: false },
      { Header: t.tableName, accessor: 'name', disableSorting: false },
      { Header: t.tableDepartment, accessor: 'departmentName', disableSorting: false },
    ],
    // поля формы для создания/редактирования сущности
    formFields: [
      {
        name: 'name',
        label: t.labelBrigade,
        placeholder: t.placeholderEnterBrigade,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'departmentId',
        label: t.labelDepartment,
        placeholder: t.placeholderDepartment,
        type: 'select',
        isMulti: false,
        required: true,
        column: 'right',
        optionsSource: 'departments',
        fetchAction: initAllDepartmentOptionsFromServer,
        ...defaultSize,
      },
    ],
  },

  /**
   * Сотрудники
   */
  employees: {
    ...defaultTextConfig(t),
    // поле, которое отобразиться в тайтл при редактировании если не передано будет использовано name
    // может быть передано в виде функции или просто строкой например surname
    titleField: (data) => `${data?.surname ?? ''} ${data?.name ?? ''}`.trim(), // поле, которое отобразиться в тайтл при редактировании если не передано будет использовано name
    resultTitleField: (data) => `${data?.surname ?? ''} ${data?.name ?? ''}`.trim() || data?.name,
    label: t.employee,
    showEditButton: true,
    showDeleteButton: false,
    apiPath: API_PATHS.employees,
    actions: createActions('employees'),
    addLabel: t?.addNewEmployee,
    titleAdd: t?.titleAddEmployee,
    titleEdit: (entity) => formatEntityTitle(t.titleEditEmployee, entity),
    titleAddResult: (entity) => formatEntityTitle(t.titleAddResultEmployee, entity),
    titleEditResult: (entity) => formatEntityTitle(t.titleEditResultEmployee, entity),
    hiddenFields: ['id'],
    excludedFields: ['departmentName', 'directorateName', 'roadName'],
    // колонки для отображения в таблице
    columns: [
      { Header: t?.tableId, accessor: 'id', disableSorting: false },
      { Header: t?.tableSurname, accessor: 'surname', disableSorting: false },
      { Header: t?.labelName, accessor: 'name', disableSorting: false },
      { Header: t?.tablePatronymic, accessor: 'patronymic', disableSorting: false },
      { Header: t?.tableContact, accessor: 'contact', disableSorting: false },
      { Header: t?.tablePosition, accessor: 'position', disableSorting: false },
      { Header: t?.tablePositionFullName, accessor: 'positionFullName', disableSorting: false },
      { Header: t?.tableDirectorate, accessor: 'directorateId', disableSorting: false },
      { Header: t?.tableDepartment, accessor: 'departmentId', disableSorting: false },
    ],
    // поля формы для создания/редактирования сущности
    formFields: [
      {
        name: 'surname',
        label: t?.labelSurname,
        placeholder: t?.placeholderSurname,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'contact',
        label: t?.labelContact,
        placeholder: t?.placeholderContact,
        type: 'text',
        required: false,
        column: 'right',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'name',
        label: t?.labelName,
        placeholder: t?.placeholderName,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'roadId',
        label: t?.tableRoad,
        placeholder: t?.placeholderRoad,
        type: 'select',
        isMulti: false,
        required: false,
        column: 'right',
        optionsSource: 'roads',
        fetchAction: getRoadsFromServer,
        dynamicRequired: isRoadRequired,
        ...defaultSize,
      },
      {
        name: 'patronymic',
        label: t?.labelPatronymic,
        placeholder: t?.placeholderPatronymic,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 20,
        ...defaultSize,
      },
      {
        name: 'directorateId',
        label: t?.tableDirectorate,
        placeholder: t?.placeholderDirectorate,
        type: 'customSelect',
        required: false,
        column: 'right',
        optionsSource: 'directorates',
        skipResetOnce: true,
        fetchOptions: initOptionsDirectoratesFromServer,
        resetFieldsArray: ['directorateId', 'departmentId'],
        keyField: 'roadId',
        messageForSelect: t?.placeholderRoad,
        dynamicRequired: isDirectorateRequired,
        ...defaultSize,
      },
      {
        name: 'positionId',
        label: t?.labelPosition,
        placeholder: t?.placeholderPosition,
        type: 'select',
        isMulti: false,
        required: false,
        column: 'left',
        optionsSource: 'positions',
        fetchAction: getPositionsFromServer,
        ...defaultSize,
      },

      {
        name: 'departmentId',
        label: t?.tableDepartment,
        placeholder: t?.placeholderDepartment,
        type: 'customSelect',
        required: false,
        column: 'right',
        optionsSource: 'departments',
        fetchOptions: initDepartmentOptionsFromServer,
        resetFieldsArray: ['departmentId'],
        keyField: 'directorateId',
        skipResetOnce: true,
        messageForSelect: t?.placeholderDirectorate,
        dynamicRequired: isDepartmentRequired,
        ...defaultSize,
      },
      {
        name: 'positionFullName',
        label: t?.labelPositionFullName,
        placeholder: t?.placeholderPositionFullName,
        type: 'text',
        required: false,
        column: 'left',
        maxLength: 20,
        dynamicRequired: isPositionFullNameRequired,
        ...defaultSize,
      },
    ],
  },

  /**
   * Email-рассылки
   */
  mailings: {
    ...defaultTextConfig(t),
    label: t?.mailing,
    showEditButton: false,
    showDeleteButton: true,
    apiPath: API_PATHS.mailings,
    actions: createActions('mailings', {
      includeDelete: true,
      overrides: {
        add: addMailingWithEmails,
        delete: deleteEmailFromMailing,
      },
    }),
    addLabel: t?.addNewEmail,
    titleAdd: t?.titleAddMailing,
    resultTitleField: (data) => `${data?.email ?? ''}`,
    titleAddResult: (entity) => formatEntityTitle(t?.titleAddResultMailing, entity),
    titleDelete: (entity) => t?.titleDeleteMailing,
    titleDeleteResult: (entity) => formatEntityTitle(t?.titleDeleteResultMailing, entity),
    confirmDeleteText: (entity) => formatEntityTitle(t?.confirmDeleteMailing, entity),
    confirmDeleteButton: t?.deleteButton,
    cancelDeleteButton: t?.cancelButtonText,
    getTableData: getRecipientsFromSelectedMailing,
    hiddenFields: ['keyName'],
    columns: [{ Header: t?.tableEmail, accessor: 'email', disableSorting: false }],
    formFields: [
      {
        name: 'email',
        label: t?.labelEmail,
        placeholder: t?.placeholderEmail,
        validate: validateEmail,
        errorText: t?.invalidEmail,
        type: 'text',
        required: true,
        column: 'center',
        maxLength: 20,
        ...defaultSize,
      },
    ],
  },

  /**
   * Пользователи
   */
  users: {
    ...defaultTextConfig(t),
    label: t?.user,
    showEditButton: true,
    showDeleteButton: true,
    apiPath: API_PATHS.users,
    actions: createActions('users', { includeDelete: true }),
    addLabel: t?.addNewUser,
    titleAdd: t?.titleAddUser,
    titleField: 'fullName',
    resultTitleField: 'fullName',
    allowedDeleteRoles: ['admin'],
    titleEdit: (entity) => formatEntityTitle(t?.titleEditUser, entity),
    titleAddResult: (entity) => formatEntityTitle(t?.titleAddResultUsers, entity),
    titleEditResult: (entity) => formatEntityTitle(t?.titleEditResultUsers, entity),
    titleDelete: () => t?.deleteUserTitle,
    titleDeleteResult: (entity) => formatEntityTitleDelete(t?.userDeleted, entity),
    confirmDeleteText: (entity) => formatEntityTitle(t?.deleteUserConfirm, entity),
    confirmDeleteButton: t?.deleteButton,
    cancelDeleteButton: t?.cancelButtonText,
    hiddenFields: ['id', 'email'],
    columns: [
      { Header: t?.tableUsername, accessor: 'username', disableSorting: false },
      { Header: t?.fullName, accessor: 'fullName', disableSorting: false },
      { Header: t?.tableRole, accessor: 'roles', disableSorting: false },
    ],
    customRender: {
      roles: (value) => (Array.isArray(value) ? value.map((role) => role.name).join(', ') : '-'),
    },
    formFields: [
      {
        name: 'email',
        sourceField: 'username', // это имя поля в initialData (initialData.name)
        label: t?.labelUsername,
        placeholder: t?.placeholderUsername,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 40,
        validate: validateEmail,
        errorText: t?.invalidEmailLogin,
        ...defaultSize,
      },
      {
        name: 'fullName',
        label: t?.fullName,
        placeholder: t?.fullNamePlaceholder,
        type: 'text',
        required: true,
        column: 'left',
        maxLength: 40,
        ...defaultSize,
      },
      {
        name: 'roleIds',
        sourceField: 'roles',
        customValue: true,
        valueNormalizer: (raw) => (Array.isArray(raw) ? raw.map((r) => r.id) : []), // если нужно преобразовать значения перед записью в объект формы
        label: t?.labelRole,
        placeholder: t?.placeholderRole,
        type: 'select',
        isMulti: false,
        required: true,
        column: 'right',
        optionsSource: 'roles',
        fetchAction: getRolesFromServer,
        ...defaultSize,
      },
    ],
  },
  /**
   * Логи
   */
  logs: {
    label: t?.historyTitle,
    hideAddButton: true,
    showEditButton: false,
    showDeleteButton: false,
    apiPath: API_PATHS.logs,
    actions: { get: () => getDataForAdmin('logs', API_PATHS.logs) },
    columns: [
      { Header: t?.adminFullName, accessor: 'adminFullName', disableSorting: false },
      { Header: t?.targetUserFullName, accessor: 'targetUserFullName', disableSorting: false },
      { Header: t?.tableAction, accessor: 'action', disableSorting: false },
      { Header: t?.tableUsernameBefore, accessor: 'beforeUsername', disableSorting: true },
      { Header: t?.tableUsernameAfter, accessor: 'afterUsername', disableSorting: true },
      { Header: t?.tableFullNameBefore, accessor: 'beforeFullName', disableSorting: true },
      { Header: t?.tableFullNameAfter, accessor: 'afterFullName', disableSorting: true },
      { Header: t?.tableRolesBefore, accessor: 'beforeRoles', disableSorting: true },
      { Header: t?.tableRolesAfter, accessor: 'afterRoles', disableSorting: true },
      { Header: t?.tableDate, accessor: 'timestamp', disableSorting: false },
    ],

    customRender: {
      action: (value) => getTranslatedAction(value, t),
      beforeRoles: (value) => (Array.isArray(value) ? value.join(', ') : value || '-'),
      afterRoles: (value) => (Array.isArray(value) ? value.join(', ') : value || '-'),
      timestamp: (value) => (value ? convertDateToString(value) : '-'), // если значение есть — форматируем, иначе дефолт
    },
  },
});
